/**
 * Browser position capture for the two applicant acts that require one: giving
 * consent to identity verification, and signing.
 *
 * Pure-ish and dependency-injected (the `geo` / `secure` parameters) for the
 * same reason `livenessQuality` and `attemptReason` are pure: the interesting
 * behaviour here is the FAILURE mapping, and jsdom has no Geolocation API to
 * fail with.
 *
 * ## The failures are the feature
 *
 * A position request fails far more often than it succeeds in the wild, and the
 * five ways it fails need five different things from the applicant. Collapsing
 * them into "location unavailable" produces the worst screen in the flow: a
 * retry button that can never work, shown to someone who has no idea what to
 * change. So each one is named and kept named all the way to the copy.
 *
 * `insecure_context` is called out separately from `unsupported` because it is
 * the one that is OUR fault: `navigator.geolocation` exists on a plain-HTTP
 * page but every call fails, and the applicant would be told to check settings
 * they cannot fix. Detecting it lets a deployment mistake read as a deployment
 * mistake.
 */

/** Why a position could not be obtained. Stable codes — they reach the copy. */
export type PositionDenial =
  | 'unsupported'
  | 'insecure_context'
  | 'permission_denied'
  | 'position_unavailable'
  | 'timeout';

/**
 * A position, in exactly the shape the backend accepts.
 *
 * The server struct is `deny_unknown_fields`, so `position.coords` must NOT be
 * spread: it also carries `altitudeAccuracy`, `heading` and `speed`, any one of
 * which would make the whole request a 400. Picking fields explicitly is also
 * the honest thing — we send what we mean to store, not everything the browser
 * happens to hand over.
 */
export interface PositionFix {
  latitude: number;
  longitude: number;
  /** Reported horizontal accuracy in metres. */
  accuracy: number;
  altitude?: number;
  /** Device clock, epoch milliseconds. */
  timestamp?: number;
}

export type PositionOutcome =
  | { ok: true; fix: PositionFix }
  | { ok: false; reason: PositionDenial };

/**
 * `maximumAge: 0` — never reuse a cached fix.
 *
 * A position is being recorded as evidence of where a person was when they
 * performed a legal act. A cached fix from earlier in the session would attest
 * to where they were when the browser last happened to look, which is a
 * different claim and an untrue one.
 *
 * `enableHighAccuracy: true` costs seconds and battery on mobile and buys the
 * GPS fix instead of the wifi estimate — the difference between ±5 m and
 * ±500 m on the same screen. Worth it exactly here, where the number is going
 * into a document.
 */
const OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 20_000,
  maximumAge: 0,
};

/** The numeric `GeolocationPositionError` codes, per the W3C spec. */
const PERMISSION_DENIED = 1;
const POSITION_UNAVAILABLE = 2;
const TIMEOUT = 3;

/**
 * Ask the browser where it is. Never rejects — every failure is a value.
 *
 * Returning a tagged outcome rather than throwing keeps the call sites from
 * conflating "the applicant declined to share their location", which is an
 * ordinary thing a person may do, with "something broke", which is not.
 */
export function requestPosition(
  geo: Geolocation | undefined = typeof navigator === 'undefined' ? undefined : navigator.geolocation,
  secure: boolean = typeof window === 'undefined' ? true : window.isSecureContext !== false,
): Promise<PositionOutcome> {
  if (!secure) return Promise.resolve({ ok: false, reason: 'insecure_context' });
  if (!geo || typeof geo.getCurrentPosition !== 'function') {
    return Promise.resolve({ ok: false, reason: 'unsupported' });
  }

  return new Promise<PositionOutcome>((resolve) => {
    // Resolve-once guard: a misbehaving implementation (or a stub) that calls
    // both callbacks must not turn a denial into a success or vice versa.
    let settled = false;
    const once = (outcome: PositionOutcome) => {
      if (settled) return;
      settled = true;
      resolve(outcome);
    };

    try {
      geo.getCurrentPosition(
        (position) => once({ ok: true, fix: toFix(position) }),
        (error) => once({ ok: false, reason: classify(error) }),
        OPTIONS,
      );
    } catch {
      // Some embedded webviews throw synchronously instead of calling the
      // error callback. Same outcome as any other unusable API.
      once({ ok: false, reason: 'unsupported' });
    }
  });
}

/** Project a `GeolocationPosition` onto the wire shape. Pure. */
export function toFix(position: GeolocationPosition): PositionFix {
  const { coords } = position;
  const fix: PositionFix = {
    latitude: coords.latitude,
    longitude: coords.longitude,
    accuracy: coords.accuracy,
  };
  // Only when the device actually reported one. `null` is the common case and
  // the field is optional server-side, so sending it would add noise, not data.
  if (typeof coords.altitude === 'number' && Number.isFinite(coords.altitude)) {
    fix.altitude = coords.altitude;
  }
  if (typeof position.timestamp === 'number' && Number.isFinite(position.timestamp)) {
    fix.timestamp = position.timestamp;
  }
  return fix;
}

/** Map a `GeolocationPositionError` onto a stable denial code. Pure. */
export function classify(error: Pick<GeolocationPositionError, 'code'>): PositionDenial {
  switch (error?.code) {
    case PERMISSION_DENIED:
      return 'permission_denied';
    case POSITION_UNAVAILABLE:
      return 'position_unavailable';
    case TIMEOUT:
      return 'timeout';
    default:
      // An unknown code is closer to "the device could not tell us" than to
      // "you said no" — and the copy for the two is different, so guessing
      // `permission_denied` would send people to settings for nothing.
      return 'position_unavailable';
  }
}

/**
 * Whether retrying in this tab can plausibly help.
 *
 * `permission_denied` is sticky per origin in every major browser: the prompt
 * does not come back, so a "Try again" button is a button that does nothing.
 * Those applicants need the site-settings instructions instead.
 */
export function isRetryable(reason: PositionDenial): boolean {
  return reason === 'timeout' || reason === 'position_unavailable';
}
